import type { BrowserName } from "@/lib/browser-detector";
import { getBrowser } from "@/lib/browser-detector";
import { getOS } from "@/lib/os-detector";

/**
 * Browsers whose built-in PDF viewer cannot render a `blob:` URL under our
 * Content-Security-Policy.
 *
 * Chromium loads the PDF plugin into a guest frame governed by `frame-src`, and
 * the deployed policy carries no `blob:`. Edge honours that and comes up empty
 * or collapsed. Chrome escapes it by handing the document to its own viewer in
 * an extension-origin document the page policy never reaches.
 *
 * **Add a browser here when the same breakage is reported.** That is the only
 * edit needed — every PDF surface reads this list through
 * `selectPdfViewerMode()` below.
 *
 * Note this cannot be decided by watching for the CSP violation itself:
 * confirmed on 2026-08-25, the violation fires in *every* browser, Chrome
 * included, so it says nothing about whether rendering will actually succeed.
 * See CSRTB-1034.
 */
export const BLOCKED_PDF_BROWSERS: BrowserName[] = ["edge"];

export type PdfViewerMode = "native" | "app";

/**
 * Picks the viewer for every PDF surface: the browser's own plugin, or the
 * application's viewer under an explanatory notice.
 *
 * Unlisted browsers get the native plugin, so a browser nobody has reported a
 * problem with keeps the better experience.
 */
export function selectPdfViewerMode(): {
  mode: PdfViewerMode;
  reason: string;
} {
  const { name } = getBrowser();
  const { isAndroid, isIOS } = getOS();
  const isMobile = isAndroid || isIOS;

  /*
   * Mobile is forced to the app viewer regardless of the list: phone browsers
   * do not render a framed PDF inline at all, they offer to download it, so a
   * native branch there shows an empty box.
   */
  const isBlockedBrowser = BLOCKED_PDF_BROWSERS.includes(name);
  const mode: PdfViewerMode = isBlockedBrowser || isMobile ? "app" : "native";

  const why = isBlockedBrowser
    ? "its built-in PDF viewer cannot render this document under the page's Content-Security-Policy"
    : isMobile
      ? "mobile browsers do not render PDFs inline"
      : "using the browser's built-in PDF viewer";
  const reason = `${name}${isMobile ? " (mobile)" : ""} — ${why}`;

  // Deliberate diagnostics: this line is how a support request establishes
  // which viewer a member's browser actually got. Informational rather than a
  // warning, hence the exemption from the console rule.
  // eslint-disable-next-line no-console
  console.info(`[pdf-viewer] Browser: ${reason}`);

  return { mode, reason };
}
